import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import Toast from "react-native-toast-message";

import Loader from "@/components/Loader";
import TitleXl from "@/components/TitleXl";
import Button from "@/components/button/Button";
import Input from "@/components/input/Input";
import { sendRequest } from "@/utils/ApiUtil";
import { AccountFees } from "@/utils/dto/accounts.request";

interface FeesListProps {
  accountData: {
    asset_type_id: string;
    available_balance: number;
    current_balance: number;
    customer_id: string;
    id: string;
    product_id: string;
    product_type: string;
    status: string;
    fee: {
      feeId?: string;
      id: string;
      ach: string;
      swift: string;
      swiftIn: string;
      domestic: string;
      domesticIn: string;
      processingFee: number;
      conversionFee: number;
    };
  };
  syncData: () => void;
}

type FormData = {
  feeId?: string;
  id?: string;
  ach: string;
  swift: string;
  swiftIn: string;
  domestic: string;
  domesticIn: string;
  processingFee: string;
  conversionFee: string;
};

export default function FeesList(props: FeesListProps) {
  const { t } = useTranslation();
  const localAccountData = props.accountData;

  const resolver = classValidatorResolver(AccountFees, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, reset, setValue, formState } =
    useForm<FormData>({
      mode: "onSubmit",
      resolver,
    });

  const { isSubmitting } = formState;

  useEffect(() => {
    if (localAccountData?.fee !== undefined && localAccountData?.fee !== null) {
      Object.keys(localAccountData.fee).forEach((key) => {
        if (
          typeof localAccountData.fee[key] === "string" &&
          localAccountData.fee[key].includes("$")
        ) {
          localAccountData.fee[key] = parseFloat(
            localAccountData.fee[key].replace("$", ""),
          );
        }
        setValue(key as keyof FormData, localAccountData.fee[key]);
      });
    }
  }, [props.accountData]);

  const updateFee = async (data: FormData) => {
    const objectToRequest = { ...data };
    objectToRequest.feeId = localAccountData.fee.id;
    delete objectToRequest.id;

    for (const key in objectToRequest) {
      if (key !== "id" && key !== "feeId") {
        if (
          typeof objectToRequest[key] === "string" &&
          objectToRequest[key].includes(",")
        ) {
          objectToRequest[key] = parseFloat(
            objectToRequest[key].replace(",", "."),
          );
        } else {
          objectToRequest[key] = parseFloat(objectToRequest[key]);
        }
      }
    }

    try {
      const request = await sendRequest(
        "post",
        "/admin/fee",
        objectToRequest,
        true,
      );
      if (request) {
        Toast.show({
          type: "success",
          visibilityTime: 3500,
          props: {
            text1: t("common:toast.success"),
            text2: t("common:toast.subtitle.feeupdate"),
          },
        });
        reset();
        props.syncData();
      }
    } catch (error) {
      console.log(error);
      reset();
    }
  };

  return (
    <View className="w-full border border-gray rounded-lg mt-4 p-4 items-center shadow">
      <View className="mb-4">
        <TitleXl text={t("management/customer:accounts.fees.title")} />
      </View>
      <View className="flex-col items-center">
        <View className="lg:flex-row">
          <View className="mr-6">
            <Input
              label={t("management/customer:accounts.fees.swift")}
              control={control}
              name="swift"
            />
          </View>
          <Input
            label={t("management/customer:accounts.fees.swiftin")}
            control={control}
            name="swiftIn"
          />
        </View>
        <View className="lg:flex-row">
          <View className="mr-6">
            <Input
              label={t("management/customer:accounts.fees.domestic")}
              control={control}
              name="domestic"
            />
          </View>
          <Input
            label={t("management/customer:accounts.fees.domesticin")}
            control={control}
            name="domesticIn"
          />
        </View>
        <View className="lg:flex-row">
          <View className="mr-6">
            <Input
              label={t("management/customer:accounts.fees.processingfee")}
              control={control}
              name="processingFee"
            />
          </View>
          <Input
            label={t("management/customer:accounts.fees.conversionfee")}
            control={control}
            name="conversionFee"
          />
        </View>
        <Input
          label={t("management/customer:accounts.fees.ach")}
          control={control}
          name="ach"
        />
      </View>
      {isSubmitting ? (
        <View className="m-4">
          <Loader />
        </View>
      ) : (
        <Button
          label={t("common:button.confirm")}
          onClick={handleSubmit(updateFee)}
        />
      )}
    </View>
  );
}
